import { useEffect, useState } from 'react';
import { SharedService } from 'app/services/SharedServices/shared-service';
import { httpSuccess } from 'app/services/serviceHelpers';

export interface IPlacementPayPackagesFilterValues {
  orderIdOpts: { id: number; name: string; value: number | string }[];
  candidateIdOpts: { id: number; name: string; value: number }[];
  candidateNameOpts: { id: number; name: string; value: string }[];
}

const emptyFilterValues: IPlacementPayPackagesFilterValues = {
  orderIdOpts: [],
  candidateIdOpts: [],
  candidateNameOpts: [],
};

interface UsePlacementPayPackagesFilterValuesContext {
  orderId?: number;
  facilityId?: number;
  candidateId?: number;
}

/** Fetches Order ID, Candidate ID, and Candidate Name filter options from placement/margin/margin-inline-details/placements/filter-values */
export function usePlacementPayPackagesFilterValues(
  context: UsePlacementPayPackagesFilterValuesContext,
  options?: { includeOrderId?: boolean; includeCandidates?: boolean },
): IPlacementPayPackagesFilterValues & { isLoading: boolean } {
  const [filterValues, setFilterValues] = useState<IPlacementPayPackagesFilterValues>(emptyFilterValues);
  const [isLoading, setIsLoading] = useState(true);

  const includeOrderId = options?.includeOrderId !== false;
  const includeCandidates = options?.includeCandidates !== false;

  const contextId = context.orderId ?? context.facilityId ?? context.candidateId;

  useEffect(() => {
    if (contextId == null || contextId <= 0) {
      setFilterValues(emptyFilterValues);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const service = SharedService.getInstance();
    service
      .getPlacementPayPackagesFilterValues({
        orderId: context.orderId ?? undefined,
        facilityId: context.facilityId ?? undefined,
        candidateId: context.candidateId ?? undefined,
      })
      .then((response: any) => {
        if (cancelled || !httpSuccess(response?.status)) {
          return;
        }
        const data = response?.data ?? {};

        const orderIds: number[] = Array.isArray(data.orderIds) ? data.orderIds : [];
        const candidates: { candidateId: number; candidateName: string }[] =
          Array.isArray(data.candidates) ? data.candidates : [];

        const orderIdOpts = includeOrderId
          ? orderIds
              .sort((a, b) => a - b)
              .map((v, idx) => ({ id: idx + 1, name: String(v), value: v }))
          : [];

        const candidateIdOpts = includeCandidates
          ? candidates
              .sort((a, b) => a.candidateId - b.candidateId)
              .map((c, idx) => ({
                id: idx + 1,
                name: `${c.candidateId} - ${c.candidateName}`,
                value: c.candidateId,
              }))
          : [];

        const candidateNameOpts = includeCandidates
          ? candidates
              .sort((a, b) => (a.candidateName || '').localeCompare(b.candidateName || ''))
              .map((c, idx) => ({
                id: idx + 1,
                name: c.candidateName,
                value: c.candidateName,
              }))
          : [];

        setFilterValues({ orderIdOpts, candidateIdOpts, candidateNameOpts });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contextId, context.orderId, context.facilityId, context.candidateId, includeOrderId, includeCandidates]);

  return { ...filterValues, isLoading };
}
